/**
 * Aritmetica de data sensivel a fuso horario (spec 4.4).
 *
 * O servidor guarda tudo em UTC, mas varias regras sao definidas em termos do
 * dia CIVIL do usuario: "hoje", "vencido", "amanha as 09:00". Essas duas coisas
 * nao coincidem — 2026-07-30T02:00Z ja e dia 30 em UTC e ainda e dia 29 em
 * America/Belem (UTC-3). Errar aqui coloca tarefa no grupo errado da tela Hoje.
 *
 * A implementacao usa `Intl.DateTimeFormat` em vez de uma dependencia de fuso:
 * a base de fusos ja existe no runtime (workerd e browser tem ICU completo), e
 * o mesmo codigo roda dos dois lados sem duplicar a tabela IANA no bundle.
 *
 * Funcoes puras: recebem o instante, devolvem o instante. Sem `Date.now()`
 * implicito — o `now` e sempre injetado para o teste ser deterministico.
 *
 * O bloco final do arquivo trata do OUTRO tipo de data do sistema: a data pura
 * ('YYYY-MM-DD') de habitos e contagens regressivas. Ela ja esta no fuso do
 * usuario, e por isso a aritmetica sobre ela nao leva fuso nenhum.
 */

import type { IsoDate } from './types.js';

export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number;
  second: number;
  /** 0 = domingo .. 6 = sabado. Alinhado com o bitmask `days_mask` dos habitos. */
  weekday: number;
}

const MS_PER_DAY = 86_400_000;

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/**
 * Construir um `Intl.DateTimeFormat` custa caro (carrega dados de fuso). Como
 * a aplicacao usa 1-2 fusos no total, um cache por fuso elimina praticamente
 * todo esse custo sem risco de crescer sem limite.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  const cached = formatterCache.get(timeZone);
  if (cached) return cached;

  const created = new Intl.DateTimeFormat('en-US', {
    timeZone,
    // h23 e nao hour12:false: com hour12:false alguns runtimes devolvem "24"
    // para meia-noite, o que quebraria a aritmetica silenciosamente.
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'short',
  });

  formatterCache.set(timeZone, created);
  return created;
}

/** Decompoe um instante UTC nas partes do calendario civil do fuso informado. */
export function partsInZone(timeZone: string, epochMs: number): ZonedParts {
  const parts = formatterFor(timeZone).formatToParts(new Date(epochMs));

  let year = 0;
  let month = 1;
  let day = 1;
  let hour = 0;
  let minute = 0;
  let second = 0;
  let weekday = 0;

  for (const part of parts) {
    switch (part.type) {
      case 'year':
        year = Number(part.value);
        break;
      case 'month':
        month = Number(part.value);
        break;
      case 'day':
        day = Number(part.value);
        break;
      case 'hour':
        hour = Number(part.value);
        break;
      case 'minute':
        minute = Number(part.value);
        break;
      case 'second':
        second = Number(part.value);
        break;
      case 'weekday':
        weekday = WEEKDAY_INDEX[part.value] ?? 0;
        break;
      default:
        break;
    }
  }

  return { year, month, day, hour, minute, second, weekday };
}

/**
 * Deslocamento do fuso, em ms, NAQUELE instante.
 *
 * Depende do instante porque fuso nao e constante: um fuso com horario de
 * verao vale -03:00 em janeiro e -02:00 em julho. America/Belem nao tem DST
 * hoje, mas tratar o offset como fixo criaria um bug latente para qualquer
 * outro fuso que a equipe venha a usar.
 */
export function zoneOffsetMs(timeZone: string, epochMs: number): number {
  const p = partsInZone(timeZone, epochMs);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  // `epochMs` pode carregar milissegundos que `asUtc` nao tem; descartar essa
  // fracao evita um offset com residuo de ate 999 ms.
  const flooredToSecond = epochMs - ((epochMs % 1000) + 1000) % 1000;
  return asUtc - flooredToSecond;
}

/**
 * Converte uma hora de parede do fuso para o instante UTC correspondente.
 *
 * A segunda passagem existe por causa do horario de verao: o offset usado para
 * converter depende do instante que so se conhece DEPOIS de converter. Uma
 * unica passagem erra em ate uma hora nos dias de transicao.
 */
export function zonedPartsToUtc(
  timeZone: string,
  parts: {
    year: number;
    month: number;
    day: number;
    hour?: number;
    minute?: number;
    second?: number;
  },
): number {
  const wallAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour ?? 0,
    parts.minute ?? 0,
    parts.second ?? 0,
  );

  const firstGuess = wallAsUtc - zoneOffsetMs(timeZone, wallAsUtc);
  return wallAsUtc - zoneOffsetMs(timeZone, firstGuess);
}

/** Primeiro instante (00:00:00) do dia civil que contem `epochMs`. */
export function startOfDayInZone(timeZone: string, epochMs: number): number {
  const p = partsInZone(timeZone, epochMs);
  return zonedPartsToUtc(timeZone, { year: p.year, month: p.month, day: p.day });
}

/** Primeiro instante do dia civil seguinte. */
export function startOfNextDayInZone(timeZone: string, epochMs: number): number {
  return addDaysInZone(timeZone, startOfDayInZone(timeZone, epochMs), 1);
}

/**
 * Ultimo instante do dia civil (23:59:59.999).
 *
 * Derivado do inicio do dia SEGUINTE em vez de "inicio + 24h": um dia com
 * transicao de horario de verao tem 23 ou 25 horas, e somar 24h fixas
 * colocaria a fronteira dentro do dia errado.
 */
export function endOfDayInZone(timeZone: string, epochMs: number): number {
  return startOfNextDayInZone(timeZone, epochMs) - 1;
}

/**
 * Soma dias preservando a HORA DE PAREDE.
 *
 * "Amanha as 09:00" precisa cair as 09:00 mesmo que o dia tenha 23 ou 25 horas.
 * Somar `dias * 86.400.000` ao epoch daria 08:00 ou 10:00 na transicao.
 */
export function addDaysInZone(timeZone: string, epochMs: number, days: number): number {
  const p = partsInZone(timeZone, epochMs);
  // Normalizar via UTC deixa o proprio Date resolver virada de mes e de ano.
  const shifted = new Date(Date.UTC(p.year, p.month - 1, p.day + days));

  return zonedPartsToUtc(timeZone, {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: p.hour,
    minute: p.minute,
    second: p.second,
  });
}

/**
 * Segunda-feira 00:00 da semana que contem `epochMs`.
 *
 * Segunda, e nao domingo: e a convencao do RFC 5545 (WKST=MO), a mesma ja
 * usada pela recorrencia, e a do calendario brasileiro impresso. Ter duas
 * definicoes de "semana" no mesmo app faria a grade da spec 9.4 discordar da
 * serie recorrente que ela mostra.
 */
export function startOfWeekInZone(timeZone: string, epochMs: number): number {
  const weekday = partsInZone(timeZone, epochMs).weekday;
  const desdeSegunda = (weekday + 6) % 7;
  return startOfDayInZone(timeZone, addDaysInZone(timeZone, epochMs, -desdeSegunda));
}

/** Primeiro instante do dia 1 do mes que contem `epochMs`. */
export function startOfMonthInZone(timeZone: string, epochMs: number): number {
  const p = partsInZone(timeZone, epochMs);
  return zonedPartsToUtc(timeZone, { year: p.year, month: p.month, day: 1 });
}

export function daysInMonth(year: number, month: number): number {
  // Dia 0 do mes seguinte = ultimo dia deste mes.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Soma meses grampeando o dia ao ultimo dia valido do mes de destino.
 *
 * 31/jan + 1 mes nao existe em fevereiro. Grampear para 28/29 e a convencao do
 * RFC 5545 e a unica que nao faz a serie pular um mes inteiro.
 */
export function addMonthsInZone(timeZone: string, epochMs: number, months: number): number {
  const p = partsInZone(timeZone, epochMs);

  const targetMonthIndex = p.month - 1 + months;
  const targetYear = p.year + Math.floor(targetMonthIndex / 12);
  const targetMonth = ((targetMonthIndex % 12) + 12) % 12;

  const lastDay = daysInMonth(targetYear, targetMonth + 1);

  return zonedPartsToUtc(timeZone, {
    year: targetYear,
    month: targetMonth + 1,
    day: Math.min(p.day, lastDay),
    hour: p.hour,
    minute: p.minute,
    second: p.second,
  });
}

/** Fixa a hora de parede do dia civil corrente, zerando minutos e segundos. */
export function atHourInZone(timeZone: string, epochMs: number, hour: number): number {
  const p = partsInZone(timeZone, epochMs);
  return zonedPartsToUtc(timeZone, { year: p.year, month: p.month, day: p.day, hour });
}

/** Data pura 'YYYY-MM-DD' do dia civil — o formato usado por habitos e countdowns. */
export function isoDateInZone(timeZone: string, epochMs: number): string {
  const p = partsInZone(timeZone, epochMs);
  return [
    String(p.year).padStart(4, '0'),
    String(p.month).padStart(2, '0'),
    String(p.day).padStart(2, '0'),
  ].join('-');
}

/** Mesmo dia civil? Comparacao por data, nao por diferenca de instantes. */
export function isSameDayInZone(timeZone: string, a: number, b: number): boolean {
  return isoDateInZone(timeZone, a) === isoDateInZone(timeZone, b);
}

/**
 * Quantidade de dias civis inteiros entre dois instantes. Positivo quando
 * `target` esta no futuro.
 *
 * Arredonda porque a diferenca entre dois inicios de dia pode nao ser multiplo
 * exato de 24h quando ha transicao de horario de verao no intervalo.
 */
export function dayDifferenceInZone(timeZone: string, from: number, target: number): number {
  const a = startOfDayInZone(timeZone, from);
  const b = startOfDayInZone(timeZone, target);
  return Math.round((b - a) / MS_PER_DAY);
}

// --------------------------------------------------------------- data pura
/**
 * Aritmetica sobre 'YYYY-MM-DD'.
 *
 * Estas funcoes NAO recebem fuso, e isso e a decisao central do bloco: a data
 * pura ja E do calendario do usuario. Converte-la para instante para poder
 * somar um dia, e converter de volta, reintroduziria exatamente a chance de
 * errar o dia que o formato existe para eliminar — e ainda faria um habito
 * "quebrar" na madrugada de uma transicao de horario de verao.
 *
 * A implementacao usa `Date.UTC`, que aqui e so uma calculadora de calendario
 * gregoriano: em UTC todo dia tem 24 h, nao existe DST, e a virada de mes e de
 * ano sai de graca.
 */
function isoDateToUtcMs(date: IsoDate): number {
  return Date.UTC(
    Number(date.slice(0, 4)),
    Number(date.slice(5, 7)) - 1,
    Number(date.slice(8, 10)),
  );
}

function utcMsToIsoDate(epochMs: number): IsoDate {
  const d = new Date(epochMs);
  return [
    String(d.getUTCFullYear()).padStart(4, '0'),
    String(d.getUTCMonth() + 1).padStart(2, '0'),
    String(d.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

/** Dia da semana da data pura. 0 = domingo, alinhado com o `days_mask`. */
export function weekdayOfIsoDate(date: IsoDate): number {
  return new Date(isoDateToUtcMs(date)).getUTCDay();
}

/** Soma (ou subtrai) dias de calendario, resolvendo virada de mes e de ano. */
export function addDaysToIsoDate(date: IsoDate, days: number): IsoDate {
  return utcMsToIsoDate(isoDateToUtcMs(date) + days * MS_PER_DAY);
}

/** Dias de calendario de `from` ate `target`. Positivo quando `target` e depois. */
export function isoDateDifference(from: IsoDate, target: IsoDate): number {
  return Math.round((isoDateToUtcMs(target) - isoDateToUtcMs(from)) / MS_PER_DAY);
}

/** Ano, mes (1-12) e dia de uma data pura, sem passar por instante. */
export function isoDateParts(date: IsoDate): { year: number; month: number; day: number } {
  return {
    year: Number(date.slice(0, 4)),
    month: Number(date.slice(5, 7)),
    day: Number(date.slice(8, 10)),
  };
}

/** Monta 'YYYY-MM-DD' a partir das partes, grampeando nada — ver `daysInMonth`. */
export function isoDateFromParts(year: number, month: number, day: number): IsoDate {
  return [
    String(year).padStart(4, '0'),
    String(month).padStart(2, '0'),
    String(day).padStart(2, '0'),
  ].join('-');
}
